/**
 * useAlerts Hook
 *
 * React Query hooks for alert management.
 *
 * Requirements:
 * - FR-701: Display alerts for backup failures, missed schedules, capacity warnings
 * - FR-702: Show alert severity (Critical/High/Medium/Low)
 * - FR-703: Operators can acknowledge alerts
 * - FR-704: Operators can resolve alerts with notes
 */

import { useQuery, useMutation, useQueryClient, UseQueryOptions } from '@tanstack/react-query';
import { apiClient } from '../services/api';

/**
 * Alert types
 */
export type AlertType =
  | 'BackupFailed'
  | 'BackupMissed'
  | 'CapacityWarning'
  | 'RTOExceeded'
  | 'ValidationFailed';
export type AlertSeverity = 'Critical' | 'High' | 'Medium' | 'Low';
export type EscalationStatus = 'None' | 'Escalated';

/**
 * Alert interface
 */
export interface Alert {
  id: string;
  type: AlertType;
  /** Backend generic-mapper also emits alertType; kept as a fallback for the detail modal. */
  alertType?: AlertType;
  severity: AlertSeverity;
  lakehouseId: string;
  lakehouseName: string;
  workspaceName: string;
  backupRunId: string | null;
  /** Migration 165: representative dbo.alert_junction connection (what triggered the alert) */
  entityType?: string | null;
  entityName?: string | null;
  connectionCount?: number;
  title: string;
  message: string;
  details: Record<string, unknown> | null;
  createdAt: string;
  acknowledgedAt: string | null;
  acknowledgedBy: string | null;
  /** Resolved display name for acknowledgedBy (detail endpoint enrichment). */
  acknowledgedByName?: string | null;
  resolvedAt: string | null;
  resolvedBy: string | null;
  /** Resolved display name for resolvedBy (detail endpoint enrichment). */
  resolvedByName?: string | null;
  resolutionNotes: string | null;
  escalationStatus: EscalationStatus;
  escalatedAt: string | null;
}

/**
 * Alert filters
 */
export interface AlertFilters {
  lakehouseId?: string;
  severity?: AlertSeverity;
  alertType?: AlertType;
  isAcknowledged?: boolean;
  isResolved?: boolean;
  isEscalated?: boolean;
  /** Spec 255: filter to alerts acknowledged (owned) by this user id. */
  acknowledgedBy?: string;
  /** Spec 255: filter to alerts resolved (cleared) by this user id. */
  resolvedBy?: string;
  startDate?: string; // ISO 8601
  endDate?: string; // ISO 8601
  page?: number;
  pageSize?: number;
  sortBy?: 'createdAt' | 'severity' | 'acknowledgedAt' | 'resolvedAt';
  sortOrder?: 'asc' | 'desc';
}

/**
 * Alert statistics
 */
export interface AlertStats {
  total: number;
  unacknowledged: number;
  unresolved: number;
  escalated: number;
  critical: number;
  high: number;
  medium: number;
  low: number;
}

/**
 * Paginated response
 */
export interface PaginatedAlerts {
  data: Alert[];
  pagination: {
    currentPage: number;
    pageSize: number;
    totalPages: number;
    totalCount: number;
  };
}

/**
 * Acknowledge alert payload
 */
export interface AcknowledgeAlertPayload {
  notes?: string;
}

/**
 * Resolve alert payload — the resolution summary plus optional additional notes.
 * Both are persisted (combined) into resolution_notes.
 */
export interface ResolveAlertPayload {
  resolution?: string;
  notes?: string;
}

/**
 * Hook to get paginated list of alerts
 */
export function useAlerts(
  filters: AlertFilters = {},
  enablePolling = false,
  pollingInterval = 10000
) {
  return useQuery<PaginatedAlerts, Error>({
    queryKey: ['alerts', filters],
    queryFn: async () => {
      const params = new URLSearchParams();

      if (filters.lakehouseId) params.append('lakehouseId', filters.lakehouseId);
      if (filters.severity) params.append('severity', filters.severity);
      if (filters.alertType) params.append('alertType', filters.alertType);
      if (filters.isAcknowledged !== undefined)
        params.append('isAcknowledged', String(filters.isAcknowledged));
      if (filters.isResolved !== undefined) params.append('isResolved', String(filters.isResolved));
      if (filters.isEscalated !== undefined)
        params.append('isEscalated', String(filters.isEscalated));
      if (filters.startDate) params.append('startDate', filters.startDate);
      if (filters.endDate) params.append('endDate', filters.endDate);
      if (filters.page) params.append('page', String(filters.page));
      if (filters.pageSize) params.append('pageSize', String(filters.pageSize));
      if (filters.sortBy) params.append('sortBy', filters.sortBy);
      if (filters.sortOrder) params.append('sortOrder', filters.sortOrder);

      return apiClient.get<PaginatedAlerts>(`/api/v1/alerts?${params.toString()}`);
    },
    refetchInterval: enablePolling ? pollingInterval : false,
    staleTime: enablePolling ? pollingInterval : 5 * 60 * 1000, // 5 minutes if not polling
  });
}

/**
 * Hook to get single alert details
 */
export function useAlertDetail(
  alertId: string | null,
  enablePolling = false,
  pollingInterval = 10000
) {
  return useQuery<Alert, Error>({
    queryKey: ['alert', alertId],
    queryFn: async () => {
      if (!alertId) throw new Error('Alert ID is required');
      return apiClient.get<Alert>(`/api/v1/alerts/${alertId}`);
    },
    enabled: !!alertId,
    refetchInterval: enablePolling ? pollingInterval : false,
    staleTime: enablePolling ? pollingInterval : 5 * 60 * 1000, // 5 minutes if not polling
  });
}

/**
 * Hook to get alert statistics
 */
export function useAlertStats(lakehouseId?: string) {
  return useQuery<AlertStats, Error>({
    queryKey: ['alertStats', lakehouseId],
    queryFn: async () => {
      const params = lakehouseId ? `?lakehouseId=${lakehouseId}` : '';
      return apiClient.get<AlertStats>(`/api/v1/alerts/stats${params}`);
    },
    staleTime: 2 * 60 * 1000, // 2 minutes
  });
}

/**
 * Hook to acknowledge an alert
 */
export function useAcknowledgeAlert() {
  const queryClient = useQueryClient();

  return useMutation<Alert, Error, { alertId: string; payload: AcknowledgeAlertPayload }>({
    mutationFn: async ({ alertId, payload }) => {
      return apiClient.post<Alert>(`/api/v1/alerts/${alertId}/acknowledge`, payload);
    },
    // The POST returns a { success, data } wrapper (not a bare Alert), so data.id is
    // undefined → the detail query never re-fetched → the modal stayed "Active" and the
    // Acknowledge button stayed clickable. Invalidate by the known alertId instead.
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({ queryKey: ['alerts'] });
      queryClient.invalidateQueries({ queryKey: ['alert', variables.alertId] });
      queryClient.invalidateQueries({ queryKey: ['alertStats'] });
    },
  });
}

/**
 * Hook to resolve an alert
 */
export function useResolveAlert() {
  const queryClient = useQueryClient();

  return useMutation<Alert, Error, { alertId: string; payload: ResolveAlertPayload }>({
    mutationFn: async ({ alertId, payload }) => {
      return apiClient.post<Alert>(`/api/v1/alerts/${alertId}/resolve`, payload);
    },
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({ queryKey: ['alerts'] });
      queryClient.invalidateQueries({ queryKey: ['alert', variables.alertId] });
      queryClient.invalidateQueries({ queryKey: ['alertStats'] });
    },
  });
}
